/**
 * The date-range preset key contract, shared by every part of the app that
 * offers quick-select periods.
 *
 * This module is deliberately PURE DATA with zero imports — no React, no
 * i18next. Two reasons:
 *
 * 1. The keys are a hard contract, not UI text. `utils/berlinDate.js`'s
 *    `getPresetRange` switches on them and throws `Unknown preset: x` on
 *    anything it doesn't know, and `presetKey` is PERSISTED into saved
 *    dashboard configs and re-resolved at request time by
 *    `services/helpers/filterRequest.js`. They must stay language-invariant
 *    forever. Keeping them in a table with no translated labels attached is
 *    what makes that obvious to the next reader.
 * 2. `features/kpi/_shared/periodPresets.js` needs the key list from a plain,
 *    non-React function (`detectPeriodPresetFromDateRange`, a migration
 *    fallback for legacy configs). A `t`-bearing table could not serve it.
 *
 * The German/English labels live in `common:datePresets.*`. Before this module
 * existed the same 14 labels were duplicated across three files with a comment
 * asking future maintainers to "keep them in sync" — this is that sync, made
 * structural.
 */

export const CALENDAR_PRESET_KEYS = [
  'today',
  'yesterday',
  'thisWeek',
  'lastWeek',
  'last2Weeks',
  'thisMonth',
  'lastMonth',
  'thisYear',
  'lastYear',
];

export const ROLLING_PRESET_KEYS = [
  'last7Days',
  'last14Days',
  'last30Days',
  'last90Days',
  'last12Months',
];

/**
 * Group descriptors. `labelKey` is fully namespace-qualified on purpose: it
 * makes this table independent of which namespace the calling component bound
 * with `useTranslation(...)`.
 */
export const DATE_PRESET_GROUPS = [
  { id: 'calendar', labelKey: 'common:datePresets.groups.calendar', keys: CALENDAR_PRESET_KEYS },
  { id: 'rolling', labelKey: 'common:datePresets.groups.rolling', keys: ROLLING_PRESET_KEYS },
];

export const ALL_DATE_PRESET_KEYS = DATE_PRESET_GROUPS.flatMap((group) => group.keys);

/**
 * The single point where a preset translation key is constructed.
 *
 * Dynamic key building is invisible to ESLint, so `datePresetKeys.test.js`
 * enumerates this function against both locale files and against
 * `getPresetRange` — keeping the construction in one place is what makes that
 * test able to cover every call site.
 */
export const presetLabelKey = (key) => `common:datePresets.labels.${key}`;
